import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck2, Plus, Loader2, ChevronLeft, ChevronRight, Lock, ArrowLeft, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { YearCalendar, CalendarLegend, DayMenu, NamedBreaks } from "@/components/YearCalendar";
import { toast } from "sonner";

export default function CalendarPage({ embedded = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "corporate_admin";
  const [year, setYear] = useState(new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [perm, setPerm] = useState({ can_edit: false, reason: null });
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null); // {date, entry, x, y}
  const [form, setForm] = useState({ date: "", end_date: "", type: "holiday", label: "" });
  const [busy, setBusy] = useState(false);
  const canEdit = perm.can_edit;

  const load = async (y = year) => {
    setLoading(true);
    try {
      const [cal, permRes] = await Promise.all([
        api.get(`/calendar?from=${y}-01-01&to=${y}-12-31`),
        api.get("/calendar/permissions"),
      ]);
      setEntries(cal.data);
      setPerm(permRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(year); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const entriesMap = useMemo(() => Object.fromEntries(entries.map((e) => [e.date, e])), [entries]);

  const onDayClick = (date, entry, e) => setMenu({ date, entry, x: e.clientX, y: e.clientY });

  const pick = async (type) => {
    const { date } = menu;
    setMenu(null);
    try {
      await api.post("/calendar", { date, type });
      load(year);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    }
  };

  const clearDay = async () => {
    const { entry } = menu;
    setMenu(null);
    try {
      await api.delete(`/calendar/${entry.id}`);
      setEntries((es) => es.filter((x) => x.id !== entry.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove");
    }
  };

  const addRange = async (e) => {
    e.preventDefault();
    if (!form.date) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await api.post("/calendar", { date: form.date, end_date: form.end_date || undefined, type: form.type, label: form.label.trim() });
      toast.success("Calendar days added");
      setForm({ date: "", end_date: "", type: "holiday", label: "" });
      load(year);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const menuLocked = menu && (!canEdit || menu.entry?.district);

  return (
    <div className="p-6 md:p-10" data-testid="calendar-page">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          {!isAdmin && !embedded && (
            <button onClick={() => nav(-1)} data-testid="calendar-back-btn"
              className="mb-2 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 transition">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          )}
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">{isAdmin ? "Attendance" : "School year"}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">School calendar</h1>
          <p className="text-sm text-slate-600 mt-1">
            {canEdit
              ? "Click any day to mark it a holiday or teachers work day. These dates are excluded when computing absences."
              : "Holidays, teachers work days and other non-school days for the year."}
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="calendar-year-nav">
          <button onClick={() => setYear((y) => y - 1)} data-testid="calendar-prev-year"
            className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center transition">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-display text-xl font-bold text-slate-900 w-16 text-center" data-testid="calendar-year">{year}</span>
          <button onClick={() => setYear((y) => y + 1)} data-testid="calendar-next-year"
            className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center transition">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      {!canEdit && perm.reason === "district" && (
        <div className="mb-5 flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="calendar-locked-banner">
          <Lock className="h-4 w-4 shrink-0" /> The school calendar is managed by your district office. You can view it, but changes must be made from the corporate console.
        </div>
      )}
      {!canEdit && perm.reason === "role" && (
        <div className="mb-5 flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600" data-testid="calendar-viewonly-banner">
          <Eye className="h-4 w-4 shrink-0 text-slate-400" /> View only — calendar changes are made by your school admins.
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <CalendarLegend />
        <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
          <CalendarCheck2 className="h-3.5 w-3.5" /> {entries.length} non-school day{entries.length === 1 ? "" : "s"} in {year}
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
      ) : (
        <>
          <YearCalendar year={year} entries={entriesMap} onDayClick={onDayClick} />
          <NamedBreaks entries={entries} />
        </>
      )}

      {canEdit && (
        <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="calendar-add-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-4">
            <Plus className="h-3.5 w-3.5" /> Add a range (breaks, long weekends)
          </div>
          <form onSubmit={addRange} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Name (optional)</span>
              <input type="text" maxLength={80} value={form.label} placeholder="e.g. Thanksgiving Break"
                     onChange={(e) => setForm({ ...form, label: e.target.value })}
                     data-testid="calendar-label" className={inputCls} />
            </label>
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">From</span>
              <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                     data-testid="calendar-from" className={inputCls} />
            </label>
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">To (optional)</span>
              <input type="date" value={form.end_date} min={form.date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                     data-testid="calendar-to" className={inputCls} />
            </label>
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Type</span>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                      data-testid="calendar-type" className={inputCls}>
                <option value="holiday">Holiday for all</option>
                <option value="teacher_workday">Teachers work day</option>
              </select>
            </label>
            <button type="submit" disabled={busy} data-testid="calendar-add-btn"
                    className="h-10 inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white hover:shadow disabled:opacity-60 transition">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
            </button>
          </form>
        </section>
      )}

      <DayMenu
        menu={menu}
        locked={menuLocked}
        lockedNote={
          perm.reason === "role"
            ? "View only — calendar changes are made by your school admins."
            : menu?.entry?.district
              ? "This day is set by your district office."
              : "Calendar editing is managed by your district office."
        }
        onPick={pick}
        onClear={clearDay}
        onClose={() => setMenu(null)}
      />
    </div>
  );
}

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
